import React, { useState } from 'react';

// ─── Constants ─────────────────────────────────────────────────────
const FLOOR_OPTIONS = [
  'Ground Floor', '1st Floor', '2nd Floor', '3rd Floor', '4th Floor',
  '5th Floor', '6th Floor', '7th Floor', '8th Floor', '9th Floor', '10th Floor'
];

const ROOM_TYPE_OPTIONS = [
  { value: '',       label: 'Select' },
  { value: 'Single', label: 'Single', beds: 1 },
  { value: 'Double', label: 'Double', beds: 2 },
  { value: 'Triple', label: 'Triple', beds: 3 },
  { value: 'Quad',   label: 'Quad',   beds: 4 },
  { value: 'Dorm',   label: 'Dorm',   beds: 6 },
];

// Empty presets - user will build their own
const DEFAULT_TYPE_PRESETS = {
  Single: [],
  Double: [],
  Triple: [],
  Quad: [],
  Dorm: [],
};

const ALL_AMENITIES = [
  'AC', 'Cooler', 'Fan',
  'Attached Washroom', 'Common Washroom',
  'WiFi', 'Geyser', 'Furniture', 'Washing Machine',
  'Induction', 'Window Side', 'Balcony',
  'Fridge', 'CCTV', 'Parking',
];

const emptyRow = () => ({
  id: Date.now() + Math.random(),
  floor: '',
  roomNumber: '',
  roomType: '',
  beds: '',
  elecCurrUnit: '',
  elecPrevReading: '',
  rent: '',
  amenities: [],
});

// ─── Simple Amenities Editor (NO standard amenities section) ───────
const SimpleAmenitiesEditor = ({ amenities, roomType, onSave, onClose }) => {
  const [selected, setSelected] = useState([...amenities]);
  const [custom, setCustom] = useState('');

  const addCustom = () => {
    const val = custom.trim();
    if (val && !selected.includes(val)) {
      setSelected(prev => [...prev, val]);
    }
    setCustom('');
  };

  const removeItem = (item) => {
    setSelected(prev => prev.filter(x => x !== item));
  };

  return (
    <div className="plm-ap-overlay" onClick={onClose}>
      <div className="plm-ap-box" onClick={e => e.stopPropagation()}>
        <div className="plm-ap-head">
          <div>
            <span className="plm-ap-title">Amenities for {roomType || 'Room'}</span>
          </div>
          <button className="plm-ap-close" onClick={onClose}>✕</button>
        </div>

        <div className="plm-ap-section-label">Add Amenity</div>
        <div className="plm-ap-custom-row">
          <input
            className="plm-ap-input"
            placeholder="e.g. AC, WiFi, Study Table, Balcony…"
            value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          />
          <button className="plm-ap-add-btn" onClick={addCustom}>+ Add</button>
        </div>

        {selected.length > 0 && (
          <div className="plm-ap-selected-list">
            <div className="plm-ap-section-label" style={{marginTop: 0}}>Selected Amenities</div>
            <div className="plm-ap-selected-tags">
              {selected.map(a => (
                <span key={a} className="plm-ap-selected-tag">
                  {a}
                  <button onClick={() => removeItem(a)}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="plm-ap-footer">
          <span className="plm-ap-count">{selected.length} selected</span>
          <button className="plm-ap-save" onClick={() => { onSave(selected); onClose(); }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Preset Editor Panel (Accordion style) ────────────────────────
const SHARING_TYPES = ROOM_TYPE_OPTIONS.filter(o => o.value !== '');

const PresetEditor = ({ presets, onPresetsChange, onSyncToExistingRooms }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState('Single');
  const [customInput, setCustomInput] = useState('');
  const [message, setMessage] = useState('');

  const currentList = presets[activeType] || [];

  const toggle = (amenity) => {
    const updated = currentList.includes(amenity)
      ? currentList.filter(a => a !== amenity)
      : [...currentList, amenity];
    onPresetsChange({ ...presets, [activeType]: updated });
    setMessage('✓ Preset saved');
    setTimeout(() => setMessage(''), 1500);
  };

  const addCustom = () => {
    const val = customInput.trim();
    if (val && !currentList.includes(val)) {
      onPresetsChange({ ...presets, [activeType]: [...currentList, val] });
      setMessage('✓ Preset saved');
      setTimeout(() => setMessage(''), 1500);
    }
    setCustomInput('');
  };

  const removeCustom = (amenity) => {
    onPresetsChange({ ...presets, [activeType]: currentList.filter(a => a !== amenity) });
    setMessage('✓ Preset saved');
    setTimeout(() => setMessage(''), 1500);
  };

  const clearAll = () => {
    onPresetsChange({ ...presets, [activeType]: [] });
    setMessage('✓ Preset cleared');
    setTimeout(() => setMessage(''), 1500);
  };

  const handleSync = () => {
    onSyncToExistingRooms(presets);
    setMessage('✓ Synced to all existing rooms!');
    setTimeout(() => setMessage(''), 2000);
  };

  const customOnes = currentList.filter(a => !ALL_AMENITIES.includes(a));

  return (
    <div className="plm-pe-accordion">
      <button 
        className={`plm-pe-accordion-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="plm-pe-accordion-title">
          🎯 Amenity Presets (Auto-applies when room type is selected)
          <span className="plm-pe-accordion-badge">
            {Object.values(presets).flat().length} amenities
          </span>
        </span>
        <span className="plm-pe-accordion-icon">{isOpen ? '▼' : '▶'}</span>
      </button>

      {isOpen && (
        <div className="plm-pe-accordion-content">
          <div className="plm-pe-info">
            <span className="plm-pe-info-text">
              💡 Define amenities per room type. These will automatically apply when user selects a room type.
              <br/>⚠️ For existing rooms, click "Sync to Existing Rooms" after updating presets.
            </span>
          </div>

          <div className="plm-pe-tabs">
            {SHARING_TYPES.map(t => (
              <button
                key={t.value}
                className={`plm-pe-tab ${activeType === t.value ? 'active' : ''}`}
                onClick={() => { setActiveType(t.value); setMessage(''); }}
              >
                {t.label}
                <span className="plm-pe-tab-count">{(presets[t.value] || []).length}</span>
              </button>
            ))}
          </div>

          <div className="plm-pe-body">
            <div className="plm-pe-section-label">Amenities  {activeType}</div>
            <div className="plm-pe-chips">
              {ALL_AMENITIES.map(a => (
                <button
                  key={a}
                  className={`plm-pe-chip ${currentList.includes(a) ? 'on' : ''}`}
                  onClick={() => toggle(a)}
                >
                  {a}
                </button>
              ))}
            </div>

            <div className="plm-pe-section-label">Custom Amenities</div>
            <div className="plm-pe-custom-row">
              <input
                className="plm-pe-input"
                placeholder="Add custom amenity..."
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              />
              <button className="plm-pe-add" onClick={addCustom}>+ Add</button>
            </div>
            
            {customOnes.length > 0 && (
              <div className="plm-pe-custom-tags">
                {customOnes.map(a => (
                  <span key={a} className="plm-pe-ctag">
                    {a}
                    <button onClick={() => removeCustom(a)}>×</button>
                  </span>
                ))}
              </div>
            )}

            <div className="plm-pe-actions-row">
              <button className="plm-pe-reset-btn" onClick={clearAll}>
                Clear All
              </button>
              <button className="plm-pe-sync-btn" onClick={handleSync}>
                Sync to Existing Rooms
              </button>
            </div>
            
            {message && <div className="plm-pe-message">{message}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Modal ────────────────────────────────────────────────────
const PGLayoutModal = ({ isOpen, onClose, selectedPG }) => {
  const [rows, setRows] = useState(() => Array.from({ length: 3 }, emptyRow));
  const [amenityFor, setAmenityFor] = useState(null);
  const [typePresets, setTypePresets] = useState(
    () => Object.fromEntries(Object.entries(DEFAULT_TYPE_PRESETS).map(([k, v]) => [k, [...v]]))
  );

  if (!isOpen) return null;

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };

      if (field === 'roomType') {
        const rt = ROOM_TYPE_OPTIONS.find(o => o.value === value);
        updated.beds = rt?.beds ? String(rt.beds) : '';
        
        // AUTO-APPLY preset amenities when room type changes
        if (value && typePresets[value]) {
          updated.amenities = [...typePresets[value]];
        } else {
          updated.amenities = [];
        }
      }

      return updated;
    }));
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);
  const deleteRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const saveAmenities = (id, amenitiesList) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, amenities: amenitiesList } : r));
  };

  // Sync presets to all existing rooms based on their room type
  const syncPresetsToExistingRooms = (presets) => {
    setRows(prev => prev.map(row => {
      if (row.roomType && presets[row.roomType]) {
        return {
          ...row,
          amenities: [...presets[row.roomType]]
        };
      }
      return row;
    }));
  };

  const handleSaveLayout = () => {
    const filledRows = rows.filter(r => r.floor || r.roomNumber || r.roomType);
    const payload = {
      pgName: selectedPG?.PGName,
      pgId: selectedPG?.PGID,
      rooms: filledRows.map(({ id, ...rest }) => rest),
    };
    console.log('=== PG Layout Saved ===', payload);
    alert(`✅ Layout saved for ${selectedPG?.PGName || 'PG'}!\nCheck console for details.`);
    onClose();
  };

  return (
    <>
      <div className="plm-overlay" onClick={onClose}>
        <div className="plm-modal" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="plm-head">
            <div>
              <h2 className="plm-title">PG Layout Manager</h2>
              {selectedPG && <p className="plm-sub">{selectedPG.PGName}</p>}
            </div>
            <button className="plm-x" onClick={onClose}>✕</button>
          </div>

          {/* Table */}
          <div className="plm-table-wrap">
            <table className="plm-table">
              <thead>
                <tr>
                  <th style={{width: 40}}>#</th>
                  <th style={{width: 120}}>Floor</th>
                  <th style={{width: 90}}>Room No.</th>
                  <th style={{width: 100}}>Room Type</th>
                  <th style={{width: 50}}>Beds</th>
                  <th style={{width: 130, whiteSpace: 'nowrap'}}>Curr Electricity Unit</th>
                  <th style={{width: 130, whiteSpace: 'nowrap'}}>Prev Electricity Reading</th>
                  <th style={{width: 90}}>Rent (₹)</th>
                  <th style={{width: 140}}>Amenities</th>
                  <th style={{width: 40}}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id} className="plm-tr">
                    <td className="plm-td plm-num">{i + 1}</td>
                    <td className="plm-td">
                      <select
                        className="plm-sel"
                        value={row.floor}
                        onChange={e => updateRow(row.id, 'floor', e.target.value)}
                      >
                        <option value="">Select floor</option>
                        {FLOOR_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                    <td className="plm-td">
                      <input
                        className="plm-inp"
                        placeholder="e.g. 101"
                        value={row.roomNumber}
                        onChange={e => updateRow(row.id, 'roomNumber', e.target.value)}
                      />
                    </td>
                    <td className="plm-td">
                      <select
                        className="plm-sel"
                        value={row.roomType}
                        onChange={e => updateRow(row.id, 'roomType', e.target.value)}
                      >
                        {ROOM_TYPE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label || o.value || 'Select'}</option>
                        ))}
                      </select>
                    </td>
                    <td className="plm-td">
                      <input
                        className="plm-inp plm-inp-c plm-inp-ro"
                        value={row.beds}
                        readOnly
                        placeholder="—"
                      />
                    </td>
                    <td className="plm-td">
                      <input
                        className="plm-inp plm-inp-r"
                        type="number"
                        placeholder="0"
                        value={row.elecCurrUnit}
                        onChange={e => updateRow(row.id, 'elecCurrUnit', e.target.value)}
                      />
                    </td>
                    <td className="plm-td">
                      <input
                        className="plm-inp plm-inp-r"
                        type="number"
                        placeholder="0"
                        value={row.elecPrevReading}
                        onChange={e => updateRow(row.id, 'elecPrevReading', e.target.value)}
                      />
                    </td>
                    <td className="plm-td">
                      <input
                        className="plm-inp plm-inp-r"
                        type="number"
                        placeholder="0"
                        value={row.rent}
                        onChange={e => updateRow(row.id, 'rent', e.target.value)}
                      />
                    </td>
                    <td className="plm-td">
                      <button
                        className={`plm-am-btn ${row.amenities.length > 0 ? 'has' : ''}`}
                        onClick={() => setAmenityFor(row.id)}
                      >
                        {row.amenities.length > 0 ? `✓ ${row.amenities.length}` : '+ Add'}
                      </button>
                    </td>
                    <td className="plm-td">
                      <button className="plm-del" onClick={() => deleteRow(row.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add row */}
          <div className="plm-bar">
            <button className="plm-add-btn" onClick={addRow}>+ Add Room</button>
            <span className="plm-count">{rows.length} room{rows.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Preset Editor - Accordion */}
          <PresetEditor
            presets={typePresets}
            onPresetsChange={setTypePresets}
            onSyncToExistingRooms={syncPresetsToExistingRooms}
          />

          {/* Footer */}
          <div className="plm-foot">
            <button className="plm-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="plm-btn-save" onClick={handleSaveLayout}>
              Save Layout
            </button>
          </div>
        </div>
      </div>

      {/* Amenities popup */}
      {amenityFor !== null && (() => {
        const row = rows.find(r => r.id === amenityFor);
        return (
          <SimpleAmenitiesEditor
            amenities={row?.amenities || []}
            roomType={row?.roomType || 'Room'}
            onSave={(list) => saveAmenities(amenityFor, list)}
            onClose={() => setAmenityFor(null)}
          />
        );
      })()}

      <style>{`
        .plm-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,.5);
          z-index: 900;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .plm-modal {
          background: #fff; border-radius: 16px;
          width: 100%; max-width: 1120px;
          max-height: 90vh;
          display: flex; flex-direction: column;
          box-shadow: 0 20px 50px rgba(0,0,0,.2);
          overflow: hidden;
        }

        /* Header */
        .plm-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #E5E7EB;
          flex-shrink: 0;
        }
        .plm-title { margin: 0 0 4px; font-size: 18px; font-weight: 700; color: #111827; }
        .plm-sub { margin: 0; font-size: 13px; color: #6B7280; }
        .plm-x {
          width: 32px; height: 32px; border-radius: 8px;
          border: 1px solid #E5E7EB; background: #fff; color: #6B7280;
          cursor: pointer; font-size: 16px;
          display: flex; align-items: center; justify-content: center;
        }
        .plm-x:hover { background: #F3F4F6; }

        /* Table */
        .plm-table-wrap { overflow: auto; flex: 1; }
        .plm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .plm-table thead tr { background: #F9FAFB; position: sticky; top: 0; }
        .plm-table th {
          padding: 10px 8px; text-align: left;
          font-size: 11px; font-weight: 700; color: #6B7280;
          text-transform: uppercase; letter-spacing: .5px;
          border-bottom: 2px solid #E5E7EB;
          white-space: nowrap;
        }
        .plm-td { padding: 8px 6px; border-bottom: 1px solid #F3F4F6; }
        .plm-tr:hover td { background: #FAFAFA; }
        .plm-num { color: #9CA3AF; font-weight: 600; text-align: center; }

        /* Inputs */
        .plm-inp, .plm-sel {
          width: 100%; padding: 6px 8px;
          border: 1.5px solid #E5E7EB; border-radius: 8px;
          font-size: 13px; background: #fff;
          outline: none; transition: all .12s;
        }
        .plm-inp:focus, .plm-sel:focus { border-color: #F06E18; box-shadow: 0 0 0 2px rgba(240,110,24,.1); }
        .plm-inp-c { text-align: center; }
        .plm-inp-r { text-align: right; }
        .plm-inp-ro { background: #F9FAFB; color: #9CA3AF; }

        /* Amenity button */
        .plm-am-btn {
          width: 100%; padding: 6px 8px;
          border: 1.5px solid #E5E7EB; border-radius: 8px;
          background: #fff; font-size: 12px; color: #6B7280;
          cursor: pointer; text-align: center; transition: all .12s;
        }
        .plm-am-btn:hover { border-color: #F06E18; color: #F06E18; }
        .plm-am-btn.has { border-color: #10B981; color: #059669; background: #F0FDF4; font-weight: 500; }

        /* Delete */
        .plm-del {
          width: 28px; height: 28px; border-radius: 6px;
          border: 1px solid #E5E7EB; background: #fff; color: #D1D5DB;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .plm-del:hover { background: #FEF2F2; color: #EF4444; border-color: #FEE2E2; }

        /* Bar */
        .plm-bar {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 16px; border-top: 1px solid #F3F4F6;
        }
        .plm-add-btn {
          padding: 6px 16px; border: 1.5px dashed #D1D5DB; border-radius: 8px;
          background: #fff; color: #6B7280; font-size: 13px; font-weight: 600; cursor: pointer;
        }
        .plm-add-btn:hover { border-color: #F06E18; color: #F06E18; background: #FFF9F5; }
        .plm-count { font-size: 12px; color: #9CA3AF; margin-left: auto; }

        /* Footer */
        .plm-foot {
          display: flex; justify-content: flex-end; gap: 10px;
          padding: 14px 20px; border-top: 1px solid #E5E7EB;
        }
        .plm-btn-cancel {
          padding: 8px 18px; border: 1.5px solid #E5E7EB; border-radius: 8px;
          background: #fff; color: #374151; font-size: 13px; font-weight: 500; cursor: pointer;
        }
        .plm-btn-cancel:hover { background: #F3F4F6; }
        .plm-btn-save {
          padding: 8px 24px; border: none; border-radius: 8px;
          background: #F06E18; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
        }
        .plm-btn-save:hover { background: #D9610F; }

        /* ── Accordion Preset Editor ── */
        .plm-pe-accordion {
          border-top: 1px solid #E5E7EB;
          flex-shrink: 0;
        }
        .plm-pe-accordion-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: #FAFAFA;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          transition: background .12s;
        }
        .plm-pe-accordion-header:hover { background: #F3F4F6; }
        .plm-pe-accordion-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .plm-pe-accordion-badge {
          font-size: 11px;
          font-weight: 500;
          padding: 2px 10px;
          background: #E5E7EB;
          border-radius: 20px;
          color: #6B7280;
        }
        .plm-pe-accordion-icon { color: #9CA3AF; font-size: 12px; }
        .plm-pe-accordion-content {
          padding: 16px;
          background: #FAFAFA;
          border-top: 1px solid #F3F4F6;
        }

        .plm-pe-info {
          background: #FEF3EC;
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 16px;
        }
        .plm-pe-info-text { font-size: 12px; color: #D9610F; }

        .plm-pe-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E5E7EB;
          margin-bottom: 16px;
        }
        .plm-pe-tab {
          padding: 6px 14px;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          font-size: 13px;
          cursor: pointer;
          color: #6B7280;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .plm-pe-tab:hover { color: #F06E18; }
        .plm-pe-tab.active { color: #F06E18; border-bottom-color: #F06E18; font-weight: 600; }
        .plm-pe-tab-count {
          font-size: 11px;
          padding: 0 6px;
          background: #F3F4F6;
          border-radius: 12px;
        }

        .plm-pe-section-label {
          font-size: 11px;
          font-weight: 700;
          color: #9CA3AF;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        .plm-pe-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }
        .plm-pe-chip {
          padding: 5px 12px;
          border: 1.5px solid #E5E7EB;
          border-radius: 24px;
          background: #fff;
          font-size: 12px;
          cursor: pointer;
          transition: all .12s;
        }
        .plm-pe-chip:hover { border-color: #F06E18; color: #F06E18; }
        .plm-pe-chip.on { background: #F06E18; border-color: #F06E18; color: #fff; }

        .plm-pe-custom-row {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        .plm-pe-input {
          flex: 1;
          padding: 7px 12px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          font-size: 12px;
          max-width: 260px;
        }
        .plm-pe-input:focus { border-color: #F06E18; outline: none; }
        .plm-pe-add {
          padding: 7px 14px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .plm-pe-add:hover { border-color: #F06E18; color: #F06E18; }

        .plm-pe-custom-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }
        .plm-pe-ctag {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #EFF6FF;
          border-radius: 20px;
          font-size: 12px;
          color: #1E40AF;
        }
        .plm-pe-ctag button {
          background: none;
          border: none;
          cursor: pointer;
          color: #93C5FD;
          font-size: 14px;
        }
        .plm-pe-ctag button:hover { color: #EF4444; }

        .plm-pe-actions-row {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }
        .plm-pe-reset-btn {
          padding: 8px 16px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          background: #fff;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
        }
        .plm-pe-reset-btn:hover { border-color: #F06E18; color: #F06E18; }
        .plm-pe-sync-btn {
          padding: 8px 20px;
          background: #10B981;
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .plm-pe-sync-btn:hover { background: #059669; }
        .plm-pe-message {
          margin-top: 12px;
          font-size: 12px;
          color: #10B981;
          font-weight: 500;
        }

        /* ── Amenities Popup ── */
        .plm-ap-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.4);
          z-index: 1000; display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .plm-ap-box {
          background: #fff; border-radius: 16px; width: 100%; max-width: 480px;
          box-shadow: 0 20px 40px rgba(0,0,0,.2);
          max-height: 90vh;
          overflow-y: auto;
        }
        .plm-ap-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px; border-bottom: 1px solid #E5E7EB;
          position: sticky; top: 0; background: #fff;
        }
        .plm-ap-title { font-size: 16px; font-weight: 700; color: #111827; }
        .plm-ap-close { background: none; border: none; font-size: 18px; cursor: pointer; color: #9CA3AF; }
        .plm-ap-section-label {
          font-size: 11px; font-weight: 700; color: #9CA3AF;
          text-transform: uppercase; margin: 12px 16px 8px;
        }
        .plm-ap-custom-row {
          display: flex; gap: 8px;
          padding: 0 16px 12px;
        }
        .plm-ap-input {
          flex: 1;
          padding: 8px 12px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          font-size: 13px;
        }
        .plm-ap-input:focus { border-color: #F06E18; outline: none; }
        .plm-ap-add-btn {
          padding: 8px 16px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .plm-ap-add-btn:hover { border-color: #F06E18; color: #F06E18; }

        .plm-ap-selected-list {
          padding: 0 16px 12px;
          border-top: 1px solid #F3F4F6;
          margin-top: 8px;
        }
        .plm-ap-selected-tags {
          display: flex; flex-wrap: wrap; gap: 8px;
        }
        .plm-ap-selected-tag {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #F06E18;
          color: #fff;
          border-radius: 20px;
          font-size: 12px;
        }
        .plm-ap-selected-tag button {
          background: none;
          border: none;
          color: #fff;
          cursor: pointer;
          font-size: 14px;
        }

        .plm-ap-footer {
          display: flex; justify-content: space-between;
          padding: 14px 16px;
          border-top: 1px solid #E5E7EB;
          background: #fff;
          position: sticky; bottom: 0;
        }
        .plm-ap-count { font-size: 13px; color: #6B7280; }
        .plm-ap-save {
          padding: 8px 24px;
          background: #F06E18;
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }
        .plm-ap-save:hover { background: #D9610F; }
      `}</style>
    </>
  );
};

export default PGLayoutModal;